import { Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { selectIsPrinting } from "features/Reports";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
} from "recharts";
import { Payload } from "recharts/types/component/DefaultTooltipContent";

const customStyles = {
  tooltipContainer: {
    backgroundColor: "rgba(255, 255, 255, 1)",
    border: "1px solid rgba(218, 218, 218, 1)",
    borderRadius: "4px",
    boxShadow: "0 5px 10px 0 rgba(0, 0, 0, 0.06)",
    padding: "10px",
    "& p": {
      margin: 0,
    },
  },
  legendContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "10px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    margin: "0 10px",
    // cursor: "pointer",
  },
  legendItemTitle: {
    color: "rgba(81, 81, 81, 1)",
    padding: "0 10px",
  },
};

interface ILegend {
  color?: string;
  dataKey?: string | number;
  inactive?: boolean;
  payload?: {
    dataKey: string;
    fill: string;
    legendColor: string;
  };
  type?: string;
  value?: string;
}
type brushDimension = {
  x?: number;
  width?: number;
};
type dataKey = { color: string; label: string; legendColor: string };
const size = {
  large: {
    width: "37",
    height: "22",
  },
  small: {
    width: "15",
    height: "10",
  },
};
export type GrUsageChart = { [x: string]: string | number; xAxisLabel: string };
interface IProps {
  dataKeys: dataKey[];
  data: GrUsageChart[];
  xAxisLabel: string;
  legendSize?: "large" | "small";
}
export default function CustomComposedChart({
  legendSize = "small",
  dataKeys,
  data,
  xAxisLabel,
}: IProps) {
  const { t, i18n } = useTranslation();
  const [updatedDataKeys, setUpdatedDataKeys] = useState<dataKey[]>([]);
  const [brushDimension, setBrushDimensions] = useState<brushDimension>({});
  const isPrinting = useAppSelector(selectIsPrinting);

  useEffect(() => {
    let updatedDataKeys = [...dataKeys];
    updatedDataKeys = updatedDataKeys.map((item) => ({
      ...item,
      legendColor: item.color,
    }));
    setUpdatedDataKeys(updatedDataKeys);
  }, [dataKeys]);

  const renderLegend = ({ payload }: { payload: ILegend[] | undefined }) => {
    return (
      <div style={customStyles.legendContainer}>
        {payload?.map((item, index) => (
          <div style={customStyles.legendItem} key={"item" + index}>
            <svg
              width={size[legendSize].width}
              height={size[legendSize].height}
            >
              <rect
                width={size[legendSize].width}
                height={size[legendSize].height}
                rx="3"
                style={{
                  fill: updatedDataKeys.find(
                    (dataK) => dataK.label === item.dataKey
                  )?.legendColor,
                }}
              />
            </svg>
            <span style={customStyles.legendItemTitle}>{item.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const legendClickHandler = (legend: ILegend) => {
    let newDataKeys = updatedDataKeys.map((item) => ({
      ...item,
      color:
        legend.dataKey === item.label
          ? item.color === "rgba(255, 255, 255,0%)"
            ? item.legendColor
            : "rgba(255, 255 ,255,0%)"
          : item.color,
      legendColor: item.legendColor,
    }));
    setUpdatedDataKeys(newDataKeys);
  };

  useEffect(() => {
    handleOrientation();
    window.addEventListener("orientationchange", handleOrientation);
    return () => {
      window.removeEventListener("orientationchange", handleOrientation);
    };
  }, []);

  const handleOrientation = () => {
    setBrushDimensions({
      width: (window.screen.width * 55) / 100,
      x:
        window.screen.width >= 1400
          ? 290
          : window.screen.width >= 1024
          ? 220
          : 140,
    });
  };

  const renderTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: any;
  }) => {
    if (active && payload?.length) {
      return (
        <Typography
          component="div"
          sx={{ ...customStyles.tooltipContainer, direction: i18n.dir() }}
        >
          <p style={{ fontSize: "14px" }}>{`${payload[0].payload.title}`}</p>
          {payload.map((item: any, index: number) => (
            <p
              key={"tooltip" + index}
              style={{
                fontSize: "11px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={{ flexGrow: 1 }}>{`${item.dataKey}:`}</span>
              <span>&nbsp; &nbsp;{payload[0].payload[item.dataKey]}</span>
            </p>
          ))}
        </Typography>
      );
    }
    return undefined;
  };

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <ComposedChart
          width={500}
          height={400}
          data={data}
          margin={{
            top: 40,
            right: 20,
            bottom: 20,
            left: 20,
          }}
          style={{ fontSize: "12px" }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="xAxisLabel" />
          <YAxis
            orientation="left"
            yAxisId="left"
            label={{
              value: t("Books Read"),
              angle: -90,
              position: "insideCenter",
              fontWeight: "bold",
              fill: "rgb(59, 59, 59)",
              fontSize: "14px",
              dx: -20,
            }}
          />
          <YAxis
            orientation="right"
            yAxisId="right"
            label={{
              value: t("Number Of Students"),
              angle: -90,
              position: "insideCenter",
              fontWeight: "bold",
              fontSize: "14px",
              fill: "rgb(59, 59, 59)",
              dx: 20,
            }}
          />
          {/* <Tooltip /> */}
          <Legend
            verticalAlign="top"
            wrapperStyle={{ top: -20 }}
            content={({ payload }) =>
              renderLegend({ payload } as {
                payload: Payload<string, string>[];
              })
            }
          />
          <Bar
            dataKey={t("Books Read")}
            barSize={50}
            fill={updatedDataKeys[0]?.color}
            yAxisId="left"
            radius={[5, 5, 0, 0]}
            isAnimationActive={!isPrinting}
            label={{
              position: "insideBottom",
              fill: "rgb(255, 255, 255)",
              fontSize: "14px",
              fontWeight: "bold",
              offset: 8,
            }}
          />
          <Line
            dataKey={t("No Of Student")}
            stroke={updatedDataKeys[1]?.color}
            strokeWidth="3px"
            yAxisId="right"
            isAnimationActive={!isPrinting}
            label={{
              position: "top",
              fontSize: "12px",
              fill: "rgb(111, 111, 111)",
              fontWeight: "bold",
              offset: 12,
            }}
            activeDot={{ r: 8 }}
            dot={{ r: 6 }}
          />
          {isPrinting ? null : (
            <Brush
              dataKey={xAxisLabel}
              height={30}
              stroke="#8884d8"
              x={brushDimension.x}
              width={brushDimension.width}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
